import React, { useEffect, useMemo, useState, createContext, useContext } from 'react';
export type CartLine = {
  lineId: string;
  itemId: string;
  name: string;
  basePrice: number;
  unitPrice: number;
  qty: number;
  modifiers: {
    id: string;
    name: string;
    price: number;
  }[];
  notes?: string;
};
type Ctx = {
  lines: CartLine[];
  count: number;
  total: number;
  addItem: (item: {
    itemId: string;
    name: string;
    basePrice: number;
    modifiers?: {
      id: string;
      name: string;
      price: number;
    }[];
    notes?: string;
  }) => void;
  decrement: (lineId: string) => void;
  removeLine: (lineId: string) => void;
  clear: () => void;
};
const CartContext = createContext<Ctx | null>(null);
const STORAGE_KEY = 'mp_customer_cart';
function loadInitial(): CartLine[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}
export function CartProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [lines, setLines] = useState<CartLine[]>(loadInitial);
  // Persist so QR diners don't lose their cart on refresh / app-switch.
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(lines));
    } catch {
      /* ignore quota / privacy-mode errors */
    }
  }, [lines]);
  const addItem = (item: {
    itemId: string;
    name: string;
    basePrice: number;
    modifiers?: {
      id: string;
      name: string;
      price: number;
    }[];
    notes?: string;
  }) => setLines(prev => {
    const mods = item.modifiers || [];
    const modIds = mods.map(m => m.id).sort().join(',');
    const note = (item.notes || '').trim();
    // Notes make a line distinct so two same-item+same-modifier orders
    // with different instructions stay as separate cart lines.
    const lineId = `${item.itemId}::${modIds}::${note}`;
    const unitPrice = item.basePrice + mods.reduce((sum, m) => sum + m.price, 0);
    const existing = prev.find(l => l.lineId === lineId);
    if (existing) {
      return prev.map(l => l.lineId === lineId ? {
        ...l,
        qty: l.qty + 1
      } : l);
    }
    return [...prev, {
      lineId,
      itemId: item.itemId,
      name: item.name,
      basePrice: item.basePrice,
      unitPrice,
      qty: 1,
      modifiers: mods,
      notes: note || undefined
    }];
  });
  const decrement = (lineId: string) => setLines(prev => prev.map(l => l.lineId === lineId ? {
    ...l,
    qty: l.qty - 1
  } : l).filter(l => l.qty > 0));
  const removeLine = (lineId: string) => setLines(prev => prev.filter(l => l.lineId !== lineId));
  const clear = () => setLines([]);
  const count = useMemo(() => lines.reduce((s, l) => s + l.qty, 0), [lines]);
  const total = useMemo(() => lines.reduce((s, l) => s + l.qty * l.unitPrice, 0), [lines]);
  return <CartContext.Provider value={{
    lines,
    count,
    total,
    addItem,
    decrement,
    removeLine,
    clear
  }} data-id="element-950">
      {children}
    </CartContext.Provider>;
}
export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}